import { Link } from "@tanstack/react-router";
import { Instagram, AtSign } from "lucide-react";
import { useEntry, useEntries, useField } from "@/lib/content";

export function Footer() {
  const f = useField();
  const brand = useEntry("footer", "brand");
  const explore = useEntries("footer", "explore");
  const exploreHeading = explore.find((e) => e.key === "heading");
  const exploreLinks = explore.filter((e) => e.key !== "heading");
  const contact = useEntry("footer", "contact");
  const legal = useEntry("footer", "legal");

  const instagramUrl = String(brand?.data.instagram_url ?? "https://instagram.com");
  const tiktokUrl = String(brand?.data.tiktok_url ?? "https://tiktok.com");

  return (
    <footer className="border-t border-border bg-charcoal mt-32 relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-chili/20 blur-3xl" aria-hidden />

      {/* Fire glow bed along the bottom */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 fire-glow z-0 opacity-40" aria-hidden />

      {/* Smoke plumes rising — subtle */}
      <div className="pointer-events-none absolute inset-0 z-0 mix-blend-screen opacity-50" aria-hidden>
        {[
          { left: "20%", delay: "0s", scale: 0.9 },
          { left: "55%", delay: "3.5s", scale: 1.1 },
          { left: "82%", delay: "6s", scale: 1 },
        ].map((s, i) => (
          <span
            key={i}
            className="smoke-plume"
            style={{
              left: s.left,
              animationDelay: s.delay,
              animationDuration: "14s",
              ["--scale" as any]: s.scale,
            }}
          />
        ))}
      </div>

      {/* Embers rising — few and slow */}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-70" aria-hidden>
        {Array.from({ length: 7 }).map((_, i) => (
          <span
            key={i}
            className="ember"
            style={{
              left: `${(i * 67) % 100}%`,
              animationDelay: `${(i * 0.9) % 7}s`,
              animationDuration: `${7 + ((i * 1.1) % 4)}s`,
              ["--dx" as any]: `${((i % 3) - 1) * 8}px`,
            }}
          />
        ))}
      </div>


      <div className="mx-auto max-w-7xl px-4 sm:px-5 md:px-8 py-20 grid gap-12 grid-cols-2 md:grid-cols-4 relative z-10">
        <div className="col-span-2 md:col-span-2">
          <h2 className="font-display text-6xl md:text-8xl text-yellow leading-none">{f(brand, "title", "TACOYA")}</h2>
          <p className="mt-4 max-w-sm text-sm text-cream/70 leading-relaxed">
            {f(brand, "blurb")}
          </p>
          <div className="mt-6 flex gap-3">
            <a
              href={instagramUrl}
              aria-label="Instagram"
              className="group relative size-12 rounded-xl border border-cream/15 bg-gradient-to-br from-cream/[0.06] to-transparent backdrop-blur-sm flex items-center justify-center text-cream/80 overflow-hidden transition-all duration-300 hover:border-yellow/60 hover:text-charcoal hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-12px_rgba(250,204,21,0.55)]"
            >
              <span aria-hidden className="absolute inset-0 bg-gradient-to-br from-yellow via-yellow to-chili opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span aria-hidden className="absolute -top-px left-3 right-3 h-px bg-gradient-to-r from-transparent via-cream/40 to-transparent" />
              <Instagram size={18} className="relative z-10 transition-transform duration-300 group-hover:scale-110" />
            </a>
            <a
              href={tiktokUrl}
              aria-label="TikTok"
              className="group relative size-12 rounded-xl border border-cream/15 bg-gradient-to-br from-cream/[0.06] to-transparent backdrop-blur-sm flex items-center justify-center text-cream/80 overflow-hidden transition-all duration-300 hover:border-yellow/60 hover:text-charcoal hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-12px_rgba(250,204,21,0.55)]"
            >
              <span aria-hidden className="absolute inset-0 bg-gradient-to-br from-yellow via-yellow to-chili opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span aria-hidden className="absolute -top-px left-3 right-3 h-px bg-gradient-to-r from-transparent via-cream/40 to-transparent" />
              <AtSign size={18} className="relative z-10 transition-transform duration-300 group-hover:scale-110" />
            </a>
          </div>

        </div>
        <div>
          <h3 className="text-xs uppercase tracking-widest text-chili-bright mb-4">{f(exploreHeading, "title", "Explore")}</h3>
          <ul className="space-y-2 text-sm text-cream/80">
            {exploreLinks.map((link) => (
              <li key={link.key}>
                <Link to={String(link.data.to ?? "/") as any} className="hover:text-yellow">{f(link, "label")}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-xs uppercase tracking-widest text-chili-bright mb-4">{f(contact, "title", "Contact")}</h3>
          <ul className="space-y-2 text-sm text-cream/80">
            <li>{f(contact, "line1")}</li>
            <li>{f(contact, "line2")}</li>
            <li>{f(contact, "email")}</li>
          </ul>
        </div>
      </div>
      <div className="relative z-10 border-t border-border py-6 px-4 sm:px-5 md:px-8 text-xs text-cream/75 flex flex-col md:flex-row justify-between max-w-7xl mx-auto gap-2">
        <span>© {new Date().getFullYear()} {f(legal, "copyright", "Tacoya. Helsinki, Finland.")}</span>
        <span className="uppercase tracking-widest">{f(legal, "tagline")}</span>
      </div>
    </footer>
  );
}
